import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import {
  clearSupabaseBrowserAuthStorage,
  isAuthEmail2faPending,
  supabase,
  trackCurrentAuthDevice,
} from '../lib/supabase';
import { connectCreatorAttribution } from '../lib/creatorGrowth';
import {
  clearImplicitAuthCallbackHash,
  clearClientAuthArtifacts,
  terminateInteractiveWerseeSession,
} from '../lib/authSessionCleanup';
import { recoverSupabaseSession } from '../lib/authSessionRecovery';
import { isIdentityHostname } from '../lib/authRouting';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  refreshAuth: () => Promise<Session | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  refreshAuth: async () => null,
  signOut: async () => {},
});

const AUTH_BOOT_MAX_MS = 2500;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const usesServerSession = typeof window !== 'undefined' && isIdentityHostname(window.location.hostname);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(!usesServerSession);
  const lastSideEffectUserRef = useRef<string | null>(null);
  const refreshPromiseRef = useRef<Promise<Session | null> | null>(null);

  const scheduleSignedInSideEffects = useCallback((nextSession: Session | null) => {
    const userId = nextSession?.user.id;
    if (!userId || lastSideEffectUserRef.current === userId) return;
    lastSideEffectUserRef.current = userId;

    // Supabase callbacks must stay synchronous. Deferring prevents auth-client
    // deadlocks when these helpers make their own Supabase requests.
    window.setTimeout(() => {
      void Promise.allSettled([
        trackCurrentAuthDevice(),
        connectCreatorAttribution(),
      ]);
    }, 0);
  }, []);

  const applySession = useCallback((nextSession: Session | null) => {
    const effectiveSession = nextSession && !isAuthEmail2faPending() ? nextSession : null;
    setSession((previous) => (
      previous?.access_token === effectiveSession?.access_token ? previous : effectiveSession
    ));
    setUser(effectiveSession?.user ?? null);

    if (effectiveSession) {
      scheduleSignedInSideEffects(effectiveSession);
    } else if (!nextSession) {
      lastSideEffectUserRef.current = null;
    }

    return effectiveSession;
  }, [scheduleSignedInSideEffects]);

  const refreshAuth = useCallback(async () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const recovery = recoverSupabaseSession(supabase, {
      clearStoredSession: () => {
        clearSupabaseBrowserAuthStorage();
        clearClientAuthArtifacts();
        // auth-js deliberately leaves a failed implicit callback hash intact.
        // Remove it so a refresh cannot replay the same rejected token forever.
        clearImplicitAuthCallbackHash();
      },
    })
      .then(applySession)
      .catch(() => {
        applySession(null);
        return null;
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    refreshPromiseRef.current = recovery;
    return recovery;
  }, [applySession]);

  useEffect(() => {
    if (usesServerSession) {
      setLoading(false);
      return;
    }
    let active = true;
    const bootTimeout = window.setTimeout(() => {
      // Public routes must never be held behind a remote auth or Realtime
      // outage. Session recovery keeps running and applies a valid session
      // when it eventually resolves.
      if (active) setLoading(false);
    }, AUTH_BOOT_MAX_MS);

    void refreshAuth().finally(() => {
      window.clearTimeout(bootTimeout);
      if (active) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // The initial stored value is validated by refreshAuth before any page
      // query is allowed to mount.
      if (event === 'INITIAL_SESSION') return;
      applySession(nextSession);
    });

    return () => {
      active = false;
      window.clearTimeout(bootTimeout);
      subscription.unsubscribe();
    };
  }, [applySession, refreshAuth, usesServerSession]);

  useEffect(() => {
    const userId = session?.user.id;
    if (!userId) return;

    const channel = supabase
      .channel(`wersee-identity:${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'identity_events', filter: `user_id=eq.${userId}` },
        () => {
          void supabase.auth.refreshSession().then(({ data, error }) => {
            if (!error) applySession(data.session);
          });
        },
      )
      .subscribe();

    return () => { void supabase.removeChannel(channel); };
  }, [applySession, session?.user.id]);

  const signOut = async () => {
    const currentUser = user;
    applySession(null);
    const microsoftLogoutUrl = await terminateInteractiveWerseeSession(supabase, currentUser);
    if (microsoftLogoutUrl) {
      window.location.replace(microsoftLogoutUrl);
    }
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, refreshAuth, signOut }}>
      {loading ? (
        <div
          role="status"
          aria-label="Loading Wersee"
          className="flex min-h-[100dvh] items-center justify-center bg-[#07070a] px-6 text-white"
        >
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="grid h-12 w-12 place-items-center rounded-2xl border border-white/10 bg-white/[0.06] text-lg font-black shadow-[0_16px_60px_rgba(124,58,237,0.22)]">
              W
            </div>
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            <p className="text-sm font-medium text-white/60">Opening Wersee…</p>
          </div>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
